<script lang="ts">
	/* eslint-disable no-alert */
	import { putObjectToUpload } from "@api/s3.js";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import { getTranslate } from "@utils/translate.js";
	import type { HTMLAttributes } from "svelte/elements";
	import imageUpload from "@core/assets/icons/image-upload.svg?raw";
	import type { UploadedFile } from "@core/schema/UploadedFile.js";
	import { UploadedFileBucket } from "@core/schema/UploadedFileBucket.js";
	import { Duration } from "@core/utils/luxon.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { maxFileSize } from "../../../config/maxFileSize.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import Button from "../common/Button.svelte";
	import Icon from "../common/Icon.svelte";
	import Media from "../common/Media.svelte";
	import translations from "./FileUploader.translations.json" with { type: "json" };

	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	let {
		currentFiles = $bindable([]),
		/* eslint-disable prefer-const */
		uploadIcon = imageUpload,
		destinationFolder,
		multiple = false,
		disabled = false,
		accept = "*/*",
		maximum = multiple ? Infinity : 1,
		bucket = UploadedFileBucket.Upload,
		required = false,
		onchange,
		...rest
		/* eslint-enable prefer-const */
	}: Omit<HTMLAttributes<HTMLDivElement>, "onchange"> & {
		accept?: string | string[];
		bucket?: Extract<UploadedFileBucket, UploadedFileBucket.Upload>;
		currentFiles?: UploadedFile[];
		destinationFolder: string;
		disabled?: boolean;
		maximum?: number;
		required?: boolean;
		uploadIcon?: string;
	} & (
			| {
					multiple: true;
					onchange?(this: void, files: UploadedFile[]): void;
			  }
			| {
					multiple?: false;
					onchange?(this: void, file: UploadedFile | undefined): void;
			  }
		) = $props();

	const globalSettings = getGlobalSettings();
	const uploadFunction = $derived(
		{
			[UploadedFileBucket.Upload]: async (key: string, file: File | Blob): Promise<UploadedFile> => {
				return await putObjectToUpload(globalSettings.PUBLIC_GRAPHQL_ENDPOINT, file, key);
			},
		}[bucket],
	);

	let alreadyUploaded: [File, UploadedFile][] = $state([]);

	const upload = createPromiseWithStatus(async (filesToUpload: File[]) => {
		return await Promise.all(
			Array.from(filesToUpload).map(async (file) =>
				uploadFunction(destinationFolder, file).then((s3File) => {
					alreadyUploaded.push([file, s3File]);
					return s3File;
				}),
			),
		);
	});

	let input = $state<HTMLInputElement | undefined>(undefined);

	function onChange(files: UploadedFile[]): void {
		if (multiple) {
			(onchange as ((this: void, files: UploadedFile[]) => void) | undefined)?.(files);
		} else {
			(onchange as ((this: void, file: UploadedFile | undefined) => void) | undefined)?.(files[0]);
		}
		alreadyUploaded = alreadyUploaded.filter(([_, s3File]) => currentFiles.some((file) => file.path === s3File.path));
	}

	onChange(currentFiles);

	async function uploadFiles(fileList: FileList): Promise<void> {
		try {
			const filesToUpload = Array.from(fileList).filter(
				(file) => !alreadyUploaded.some(([f]) => `${f.name}.${f.type}` === `${file.name}.${f.type}`),
			);

			if (maximum && filesToUpload.length + currentFiles.length > maximum) {
				alert(`Maximální počet souborů je ${maximum}`);
				return;
			}

			for (const file of filesToUpload) {
				if (file.size > maxFileSize) {
					alert(
						`Soubor ${file.name} je příliš velký (${Math.round(file.size / 1024 / 1024)}). Maximální povolená velikost souboru je ${maxFileSize / 1024 / 1024} MB.`,
					);
					return;
				}

				const allowedFileTypes = Array.isArray(accept) ? accept : accept.split(",");
				if (accept !== "*/*" && !allowedFileTypes.some((type) => file.type.startsWith(type))) {
					alert(`Soubor '${file.name}' má nepodporovaný typ.`);
					return;
				}
			}

			const files = await upload.call(filesToUpload);
			if (multiple) {
				currentFiles = [...currentFiles, ...files];
			} else {
				// eslint-disable-next-line require-atomic-updates
				currentFiles = files;
			}
			onChange(currentFiles);
		} finally {
			if (input) {
				input.value = "";
			}
		}
	}

	function onDelete(file: UploadedFile): void {
		if (input) {
			input.value = "";
		}
		currentFiles = currentFiles.filter((search) => search.path !== file.path);
		onChange(currentFiles);
	}

	async function getAudioLength(file: UploadedFile): Promise<number> {
		const [inputFile, _] = alreadyUploaded.find(([_, s3File]) => s3File.path === file.path) ?? [];
		if (!inputFile) {
			return 0;
		}

		const audio = document.createElement("audio");
		audio.preload = "metadata";

		return await new Promise((resolve) => {
			audio.addEventListener("loadedmetadata", () => {
				resolve(audio.duration);
			});
			audio.src = URL.createObjectURL(inputFile);
		});
	}

	function onInputChange({ target }: Event): void {
		const input = target as HTMLInputElement;
		if (input.files) {
			void uploadFiles(input.files);
		}
	}

	const isDisabled = $derived(disabled || upload.value.status === "loading");
	let loadingInput = $state<HTMLInputElement | null>(null);
	$effect(() => {
		if (loadingInput) {
			loadingInput.setCustomValidity("Vyčkejte prosím na nahrání souboru");
		}
	});
</script>

<div {...rest} class={["relative flex flex-col gap-4", rest.class]}>
	{#if upload.value.status === "loading"}
		<input bind:this={loadingInput} type="text" required class="absolute opacity-0" tabindex="-1" />
	{/if}
	{#if currentFiles.length < maximum}
		<div
			class="border-2 [&:has(input:user-invalid)]:outline outline-black border-dashed outline-offset-2 outline-2 border-secondary bg-black text-white font-normal rounded-lg relative flex h-32 w-full items-center justify-center p-4"
		>
			<input
				bind:this={input}
				type="file"
				class="absolute inset-0 opacity-0"
				class:cursor-pointer={!isDisabled}
				class:cursor-not-allowed={isDisabled}
				accept={Array.isArray(accept) ? accept.join(",") : accept}
				disabled={isDisabled}
				{multiple}
				onchange={onInputChange}
				{required}
			/>
			<div class="pointer-events-none flex h-full w-full items-center justify-center">
				{#if upload.value.status === "loading"}
					<span>{translate("loading")}</span>
				{:else}
					<span class="flex items-center gap-4">
						<Icon class="h-[2.875rem]" icon={uploadIcon} />
						<span class="leading-[1.2]">
							{translate("drag")}
							{#if multiple}
								{translate("files")}
							{:else}
								{translate("file")}
							{/if}
							{translate("or")}
							<span class="block underline">{translate("uploadFromLocalStorage")}</span>
						</span>
					</span>
				{/if}
			</div>
		</div>
	{/if}
	{#if currentFiles.length > 0}
		<div class="flex flex-col gap-2">
			{#each currentFiles as file (file.path)}
				<div
					class="border-2 border-secondary border-dashed text-white bg-black rounded-lg p-4 flex items-center gap-2 overflow-clip"
				>
					{#if file.type.startsWith("image/")}
						<Media
							source={file}
							class="aspect-square w-24 object-contain"
							alt={file.title ?? "Náhled nahraného souboru"}
							lightbox
							lightboxWidth={600}
							height={96}
							width={96}
						/>
					{/if}
					<div class="flex-1">
						<span class="inline-block text-sm max-w-48 break-all text-ellipsis overflow-clip">{file.title}</span>
					</div>
					{#if file.type.startsWith("audio/")}
						{#await getAudioLength(file) then length}
							<span>
								{Duration.fromObject({ seconds: length }, { locale: tenant.locale }).toFormat("mm:ss")}
							</span>
						{/await}
					{/if}
					<Button
						class="h-auto !w-12 grid place-items-center !min-w-0 !aspect-square leading-none !p-0"
						variant={ButtonVariant.Secondary}
						onclick={(): void => {
							onDelete(file);
						}}
					>
						×
					</Button>
				</div>
			{/each}
		</div>
	{/if}
</div>
